import { Vue, Component, Prop, Watch } from "vue-property-decorator";
import { statistics } from "@/@types/statistics";

import PieChart from "@/components/charts/PieChart/PieChart.vue";
import BarChart from "@/components/charts/BarChart/BarChart.vue";
import { EChartsOption } from "echarts";
import { formatWeight } from "@/utils/formatter";

/**
 * Typology classes carried by `SWF_class`. Verified against the published data:
 * 1 = linear, 2 = patchy, 3 = riparian.
 * The colours follow the application palette; the authoritative SLD colours are not
 * retrievable with a non-administrator account (GetStyles unsupported, REST returns 403).
 */
const SWF_CLASSES: { value: number, label: string, color: string }[] = [
    { value: 1, label: 'Linear', color: '#1fa295' },
    { value: 2, label: 'Patchy', color: '#155a9a' },
    { value: 3, label: 'Riparian', color: '#7a5c8e' },
];

const UNCLASSIFIED_LABEL = 'Unclassified';
const UNCLASSIFIED_COLOR = '#9e9e9e';
const SQUARE_METRES_PER_HECTARE = 10000;

@Component({ components: { PieChart, BarChart } })
export default class Statistics extends Vue {
    @Prop({ required: true, default: () => [] })
    features: geoserver.feature<wfsDataLayer.properties, wfsDataLayer.geometry>[]

    @Prop({ required: true, default: false })
    loading: boolean

    statistics: statistics.data = null;

    created() {
        this.makeChart(this.features);
    }

    @Watch("features")
    onFeaturesChange() {
        this.statistics = null;
        this.$nextTick(() => {
            this.makeChart(this.features);
        })
    }

    formatWeight(value: any, c?: number, d?: string, t?: string) {
        return formatWeight(value, c, d, t);
    }

    private classLabel(value: any): string {
        return SWF_CLASSES.find(cls => cls.value === Number(value))?.label ?? UNCLASSIFIED_LABEL;
    }

    private toEntries(values: geoserver.feature<wfsDataLayer.properties, wfsDataLayer.geometry>[]): statistics.classEntry[] {
        const entries: statistics.classEntry[] = SWF_CLASSES.map(cls => ({
            label: cls.label,
            color: cls.color,
            count: 0,
            area: 0
        }));
        const unclassified: statistics.classEntry = {
            label: UNCLASSIFIED_LABEL,
            color: UNCLASSIFIED_COLOR,
            count: 0,
            area: 0
        };

        for (const feature of values) {
            const entry = entries.find(e => e.label === this.classLabel(feature.properties?.SWF_class)) ?? unclassified;
            entry.count += 1;
            entry.area += Number(feature.properties?.area_mq) || 0;
        }

        if (unclassified.count > 0) {
            entries.push(unclassified);
        }

        return entries.filter(entry => entry.count > 0);
    }

    private classCountOption(entries: statistics.classEntry[]): EChartsOption {
        return {
            legend: { top: '0px', left: 'center' },
            series: [
                {
                    type: 'pie',
                    radius: ['40%', '80%'],
                    avoidLabelOverlap: false,
                    label: { show: false, position: 'center' },
                    emphasis: {
                        label: { show: true, fontSize: 24, fontWeight: 'bold' }
                    },
                    labelLine: { show: false },
                    data: entries.map(entry => ({ value: entry.count, name: entry.label }))
                }
            ]
        } as EChartsOption;
    }

    private classAreaOption(entries: statistics.classEntry[]): EChartsOption {
        return {
            tooltip: {
                trigger: 'axis',
                valueFormatter: (value: number) => {
                    return `${!!value ? formatWeight(value, 0) : ""} ha`;
                }
            },
            legend: { show: false },
            xAxis: { type: 'category', data: entries.map(entry => entry.label) },
            yAxis: {
                type: 'value',
                axisLabel: {
                    show: true,
                    formatter: (value) => {
                        return `${!!value ? formatWeight(value, 0) : ""} ha`;
                    }
                }
            },
            series: [{
                name: 'Surface', type: 'bar',
                data: entries.map(entry => ({
                    value: entry.area / SQUARE_METRES_PER_HECTARE,
                    itemStyle: { color: entry.color }
                }))
            }]
        } as EChartsOption;
    }

    public makeChart(values: geoserver.feature<wfsDataLayer.properties, wfsDataLayer.geometry>[]) {
        const entries = this.toEntries(values ?? []);
        const totalArea = entries.reduce((total, entry) => total + entry.area, 0);

        this.statistics = {
            entries,
            featureCount: (values ?? []).length,
            totalArea,
            classCount: entries.length > 0 ? this.classCountOption(entries) : null,
            classArea: entries.length > 0 ? this.classAreaOption(entries) : null,
        }
    }
}
